/**
 * ReviewPublish Step Component
 *
 * Step 8 of listing wizard: review all details and publish.
 */

import React from 'react';
import { ListingFormData } from '../../hooks';
import { useTaxonomies } from '../../hooks';

interface ReviewPublishProps {
  data: ListingFormData;
  onGoToStep: (step: number) => void;
}

export const ReviewPublish: React.FC<ReviewPublishProps> = ({
  data,
  onGoToStep,
}) => {
  const { data: taxonomies } = useTaxonomies();

  const getPropertyTypeName = (slug: string) =>
    taxonomies?.propertyTypes.find((t) => t.slug === slug)?.name || slug;

  const getLocationDisplay = () => {
    const parts = [
      data.location.city &&
        taxonomies?.cities.find((c) => c.slug === data.location.city)?.name,
      taxonomies?.areas.find((a) => a.slug === data.location.area)?.name,
      taxonomies?.states.find((s) => s.slug === data.location.state)?.name,
    ].filter(Boolean);
    return parts.join(', ') || 'Not set';
  };

  const selectedAmenities = data.amenityIds
    .map((id) => taxonomies?.amenities.find((a) => a.id === id)?.name)
    .filter(Boolean);

  const roomTypeLabels: Record<string, string> = {
    entire_place: 'Entire place',
    private_room: 'Private room',
    shared_room: 'Shared room',
  };

  // Validation checks
  const validationIssues: string[] = [];
  if (!data.title) validationIssues.push('Title is required');
  if (!data.description) validationIssues.push('Description is required');
  if (!data.propertyType) validationIssues.push('Property type is required');
  if (!data.location.state) validationIssues.push('Location is required');
  if (data.photos.length < 3) validationIssues.push('At least 3 photos are required');
  if (!data.pricing.nightlyRate) validationIssues.push('Nightly rate is required');

  const isValid = validationIssues.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Review your listing
        </h3>
        <p className="text-gray-600">
          Make sure everything looks good before publishing.
        </p>
      </div>

      {/* Validation Status */}
      {!isValid && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertIcon className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800">Cannot publish yet</h4>
              <ul className="text-sm text-red-600 mt-1 space-y-1">
                {validationIssues.map((issue, idx) => (
                  <li key={idx}>• {issue}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {isValid && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckIcon className="w-5 h-5 text-green-500" />
            <div>
              <h4 className="font-medium text-green-800">Ready to publish!</h4>
              <p className="text-sm text-green-600">
                Your listing has all required information.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Section: Basics */}
      <ReviewSection
        title="Property basics"
        onEdit={() => onGoToStep(0)}
      >
        <ReviewRow label="Title" value={data.title || 'Not set'} />
        <ReviewRow label="Property type" value={getPropertyTypeName(data.propertyType)} />
        <ReviewRow label="Room type" value={roomTypeLabels[data.roomType]} />
        <ReviewRow
          label="Capacity"
          value={`${data.bedrooms} bedroom${data.bedrooms !== 1 ? 's' : ''}, ${data.beds} bed${data.beds !== 1 ? 's' : ''}, ${data.bathrooms} bathroom${data.bathrooms !== 1 ? 's' : ''}`}
        />
        <ReviewRow label="Max guests" value={data.maxGuests.toString()} />
        <ReviewRow
          label="Description"
          value={data.description ? `${data.description.substring(0, 100)}...` : 'Not set'}
        />
      </ReviewSection>

      {/* Section: Location */}
      <ReviewSection
        title="Location"
        onEdit={() => onGoToStep(1)}
      >
        <ReviewRow label="Location" value={getLocationDisplay()} />
        <ReviewRow
          label="Address"
          value={data.location.address || 'Not provided'}
        />
        <ReviewRow
          label="Map pin"
          value={data.location.coordinates ? 'Set' : 'Not set'}
        />
      </ReviewSection>

      {/* Section: Amenities */}
      <ReviewSection
        title="Amenities"
        onEdit={() => onGoToStep(2)}
      >
        <ReviewRow
          label="Selected amenities"
          value={
            selectedAmenities.length > 0
              ? selectedAmenities.slice(0, 5).join(', ') +
                (selectedAmenities.length > 5
                  ? ` +${selectedAmenities.length - 5} more`
                  : '')
              : 'None selected'
          }
        />
      </ReviewSection>

      {/* Section: Photos */}
      <ReviewSection
        title="Photos"
        onEdit={() => onGoToStep(3)}
      >
        <div className="grid grid-cols-4 gap-2 mb-2">
          {data.photos.slice(0, 4).map((photo, idx) => (
            <div
              key={photo.id}
              className="aspect-square rounded-lg overflow-hidden bg-gray-100"
            >
              <img
                src={photo.url}
                alt={`Photo ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
        <ReviewRow
          label="Total photos"
          value={`${data.photos.length} photo${data.photos.length !== 1 ? 's' : ''}`}
          warning={data.photos.length < 3}
        />
      </ReviewSection>

      {/* Section: Pricing */}
      <ReviewSection
        title="Pricing"
        onEdit={() => onGoToStep(4)}
      >
        <ReviewRow
          label="Nightly rate"
          value={data.pricing.nightlyRate ? `$${data.pricing.nightlyRate}` : 'Not set'}
          warning={!data.pricing.nightlyRate}
        />
        {data.pricing.weekendRate && (
          <ReviewRow
            label="Weekend rate"
            value={`$${data.pricing.weekendRate}`}
          />
        )}
        {data.pricing.cleaningFee && (
          <ReviewRow
            label="Cleaning fee"
            value={`$${data.pricing.cleaningFee}`}
          />
        )}
        {data.pricing.weeklyDiscount && (
          <ReviewRow
            label="Weekly discount"
            value={`${data.pricing.weeklyDiscount}%`}
          />
        )}
      </ReviewSection>

      {/* Section: Booking Settings */}
      <ReviewSection
        title="Booking settings"
        onEdit={() => onGoToStep(5)}
      >
        <ReviewRow
          label="Instant Book"
          value={data.instantBooking ? 'Enabled' : 'Disabled'}
        />
        <ReviewRow
          label="Stay duration"
          value={`${data.minNights}-${data.maxNights} nights`}
        />
        <ReviewRow
          label="Check-in / Check-out"
          value={`${formatTime(data.checkInTime)} / ${formatTime(data.checkOutTime)}`}
        />
      </ReviewSection>

      {/* Section: House Rules */}
      <ReviewSection
        title="House rules"
        onEdit={() => onGoToStep(6)}
      >
        <ReviewRow
          label="Smoking"
          value={data.houseRules.smokingAllowed ? 'Allowed' : 'Not allowed'}
        />
        <ReviewRow
          label="Pets"
          value={data.houseRules.petsAllowed ? 'Allowed' : 'Not allowed'}
        />
        <ReviewRow
          label="Parties"
          value={data.houseRules.partiesAllowed ? 'Allowed' : 'Not allowed'}
        />
        <ReviewRow
          label="Cancellation policy"
          value={data.cancellationPolicy.charAt(0).toUpperCase() + data.cancellationPolicy.slice(1)}
        />
      </ReviewSection>

      {/* Cover Photo Preview */}
      {data.photos.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="font-medium text-gray-900 mb-3">Listing preview</h4>
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="aspect-video relative">
              <img
                src={data.photos[0].url}
                alt="Cover"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <h5 className="font-semibold text-gray-900">{data.title || 'Your listing title'}</h5>
              <p className="text-sm text-gray-500 mt-1">{getLocationDisplay()}</p>
              <p className="text-lg font-semibold mt-2">
                ${data.pricing.nightlyRate || 0} <span className="text-sm font-normal text-gray-500">/ night</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Review Section Component
interface ReviewSectionProps {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({ title, onEdit, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden">
    <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
      <h4 className="font-medium text-gray-900">{title}</h4>
      <button
        type="button"
        onClick={onEdit}
        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
      >
        Edit
      </button>
    </div>
    <div className="p-4 space-y-2">{children}</div>
  </div>
);

// Review Row Component
interface ReviewRowProps {
  label: string;
  value: string;
  warning?: boolean;
}

const ReviewRow: React.FC<ReviewRowProps> = ({ label, value, warning }) => (
  <div className="flex justify-between items-start">
    <span className="text-sm text-gray-500">{label}</span>
    <span
      className={`text-sm text-right max-w-[60%] ${
        warning ? 'text-yellow-600' : 'text-gray-900'
      }`}
    >
      {value}
    </span>
  </div>
);

// Helper function
const formatTime = (time: string) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Icons
const AlertIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

export default ReviewPublish;
